import React, { useState, useEffect, useRef } from 'react';
import { useAlert } from 'react-alert';
import { LuActivity, LuUser } from "react-icons/lu";
import { makeApiCall } from './AssessmentCall';
import { DatePicker } from 'rsuite';
import { GenerateAndUploadPDF } from '../DialysisFlow/GenerateAndUploadPDF';

const TiltTable = ({ data, handleSuccess, mode = 'create', existingData = null }) => {
    const alert = useAlert();

    // Store initial states
    const initialInfo = {
        therapy_consultation: data?.therapy_id || '',
        id: data?.id || '',
        name: data?.name || '',
        age: data?.age || '',
        gender: data?.gender || '',
        diagnosis: '',
        therapy_type: 'general'
    };
    const initialMeasurements = {
        0: { time: '', pulseRate: '', bp: '', spo2: '' },
        10: { time: '', pulseRate: '', bp: '', spo2: '' },
        20: { time: '', pulseRate: '', bp: '', spo2: '' },
        30: { time: '', pulseRate: '', bp: '', spo2: '' },
        40: { time: '', pulseRate: '', bp: '', spo2: '' },
        50: { time: '', pulseRate: '', bp: '', spo2: '' },
        60: { time: '', pulseRate: '', bp: '', spo2: '' },
        70: { time: '', pulseRate: '', bp: '', spo2: '' },
        80: { time: '', pulseRate: '', bp: '', spo2: '' },
        90: { time: '', pulseRate: '', bp: '', spo2: '' }
    };

    const [info, setInfo] = useState(initialInfo);
    const [loading, setLoading] = useState(false);
    const [measurements, setMeasurements] = useState(initialMeasurements);

    const elementRef = useRef()
    const degrees = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90];

    // Populate data when in view mode or when existingData is provided
    useEffect(() => {
        if (existingData && existingData.recordings) {
            const populatedMeasurements = { ...measurements };

            // Set therapy type from existing data
            setInfo(prev => ({
                ...prev,
                therapy_type: existingData.assessment_type || ''
            }));

            // Populate measurements from recordings
            existingData.recordings.forEach(recording => {
                if (populatedMeasurements[recording.degree] !== undefined) {
                    populatedMeasurements[recording.degree] = {
                        time: recording.time || '',
                        pulseRate: recording.pulse_rate ? String(recording.pulse_rate) : '',
                        bp: recording.bp || '',
                        spo2: recording.spo2 ? String(recording.spo2) : ''
                    };
                }
            });

            setMeasurements(populatedMeasurements);
        }
    }, [existingData]);

    const handleInputChange = (degree, field, value) => {
        // Only allow editing in create or edit mode
        if (mode === 'view') return;

        setMeasurements(prev => ({
            ...prev,
            [degree]: {
                ...prev[degree],
                [field]: value
            }
        }));
    };

    const handleChange = (e) => {
        // Only allow editing in create or edit mode
        if (mode === 'view') return;

        const { name, value } = e.target;
        setInfo(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCancel = () => {
        if (mode === 'view') return;
        setInfo({
            ...initialInfo,
            therapy_consultation: data?.therapy_id || '',
            id: data?.id || '',
            name: data?.name || '',
            age: data?.age || '',
            gender: data?.gender || '',
            diagnosis: '',
            therapy_type: 'general'
        });

        setMeasurements(initialMeasurements);

        setLoading(false);

        alert.info('Form has been reset to initial state');
    };

    const handleSubmit = async () => {
        setLoading(true)
        const pdfResult = await GenerateAndUploadPDF(elementRef, +info.id, +info.id, 'tilt');

        if (!pdfResult.success) {
            alert.error(pdfResult.error || 'Failed to generate PDF');
            setLoading(false);
            return;
        }

        const recordings = degrees.map(degree => ({
            degree: degree,
            time: measurements[degree].time || null,
            pulse_rate: measurements[degree].pulseRate ? +measurements[degree].pulseRate : null,
            bp: measurements[degree].bp || null,
            spo2: measurements[degree].spo2 ? +measurements[degree].spo2 : null,
        }));

        const payload = {
            patient: +info.id,
            recording_scale: "tilt",
            therapy_consultation: info.therapy_consultation,
            diagnosis: info.diagnosis,
            assessment_type: info.therapy_type,
            assessment_form: info.therapy_type,
            assessment_id: info.therapy_consultation,
            tilt_document: pdfResult.document,
            recordings,
        };

        try {
            const endpoint = '/api/v1/patient/therapy-scale/';

            const method = 'post';

            const result = await makeApiCall({
                method,
                endpoint,
                data: payload,
            });

            if (result.success) {
                alert.success(`Assessment ${mode === 'edit' ? 'updated' : 'saved'} successfully!`);
                handleSuccess && handleSuccess();
                setLoading(false)
            } else {
                alert.error(result.error || `Failed to ${mode === 'edit' ? 'update' : 'save'} assessment`);
                setLoading(false)
            }
        } catch (err) {
            console.error(err);
            alert.error(`Error while ${mode === 'edit' ? 'updating' : 'saving'} assessment`);
            setLoading(false)
        }
    };

    const isReadOnly = mode === 'view';

    return (
        <div className='assessment-section' >
            <div className="general-form-content" ref={elementRef}>
                <div className="section-header">
                    <h2>VITAL SIGNS MEASUREMENT FORM</h2>
                    {mode === 'view' && <span className="mode-indicator">(View Mode)</span>}
                </div>

                <div className="section patient-information">
                    <div className="section-header">
                        <LuUser className="section-icon" />
                        <h3>Demographic data</h3>
                    </div>
                    <div className="input-row">
                        <div className="input-group-physio">
                            <label>Patient ID</label>
                            <input
                                type="text"
                                value={info?.id}
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div className="input-group-physio">
                            <label>Name</label>
                            <input
                                type="text"
                                value={info?.name}
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div className="input-group-physio">
                            <label>Age</label>
                            <input
                                type="number"
                                value={info?.age}
                                readOnly={isReadOnly}
                            />
                        </div>
                    </div>
                    <div className="input-row">
                        <div className="input-group-physio">
                            <label>Gender</label>
                            <input value={info?.gender} disabled readOnly />
                        </div>
                        <div className="input-group-physio">
                            <label>Diagnosis</label>
                            <input
                                type="text"
                                name="diagnosis"
                                value={info.diagnosis}
                                onChange={handleChange}
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div className="input-group-physio">
                            <label>Therapy Type</label>
                            <select
                                name="therapy_type"
                                value={info.therapy_type}
                                onChange={handleChange}
                                readOnly={isReadOnly}
                            >
                                <option value="general">General</option>
                                <option value="neuro">Neuro</option>
                                <option value="ortho">Ortho</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="assessment-table">
                    <table className="tilt-table">
                        <thead>
                            <tr>
                                <th>Degree</th>
                                <th>Time</th>
                                <th>Pulse Rate<br />bpm</th>
                                <th>BP (MM of Hg)</th>
                                <th>SPO2(%)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {degrees.map((degree) => (
                                <tr key={degree} className="tilt-assessment-row">
                                    <td className="catgory-cell">
                                        <strong>{degree}</strong>
                                    </td>
                                    <td className="input-cell" lang="en-GB">
                                        <DatePicker
                                            format="HH:mm:ss"
                                            value={measurements[degree].time ? new Date(`1970-01-01T${measurements[degree].time}`) : null}
                                            onChange={(val) => {
                                                const formatted = val ? val.toTimeString().slice(0, 8) : '';
                                                handleInputChange(degree, 'time', formatted);
                                            }}
                                            onClean={() => handleInputChange(degree, 'time', '')}
                                            hideHours={() => false}
                                            hideMinutes={() => false}
                                            placeholder="HH:MM:SS"
                                            placement="topStart"
                                            appearance="default"
                                            showMeridian={false}
                                            style={{ width: '100%', border: 'none' }}
                                            disabled={isReadOnly}
                                        />
                                    </td>
                                    <td className="input-cell">
                                        <input
                                            type="number"
                                            value={measurements[degree].pulseRate}
                                            onChange={(e) => handleInputChange(degree, 'pulseRate', e.target.value)}
                                            placeholder="bpm"
                                            className="table-input"
                                            min="0"
                                            max="200"
                                            readOnly={isReadOnly}
                                        />
                                    </td>
                                    <td className="input-cell">
                                        <input
                                            type="text"
                                            value={measurements[degree].bp}
                                            onChange={(e) => handleInputChange(degree, 'bp', e.target.value)}
                                            placeholder="120/80"
                                            className="table-input"
                                            readOnly={isReadOnly}
                                        />
                                    </td>
                                    <td className="input-cell">
                                        <input
                                            type="number"
                                            value={measurements[degree].spo2}
                                            onChange={(e) => handleInputChange(degree, 'spo2', e.target.value)}
                                            placeholder="%"
                                            className="table-input"
                                            min="0"
                                            max="100"
                                            readOnly={isReadOnly}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="note-section">
                    <p className="assessment-note">
                        <strong>Instructions:</strong>
                        <br />• Record measurements at each specified degree/angle
                        <br />• Use 24-hour format for time (HH:MM)
                        <br />• Enter BP as systolic/diastolic (e.g., 120/80)
                        <br />• SPO2 should be recorded as percentage value
                        <br />• Ensure patient is stable for at least 2 minutes before recording at each position
                    </p>
                </div>
            </div>
            <div className="general-form-actions">
                <button
                    type="button"
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isReadOnly}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="submit-btn"
                    onClick={handleSubmit}
                    disabled={loading || isReadOnly}
                    style={{ cursor: loading ? 'wait' : 'pointer' }}
                >
                    {loading ? 'Saving...' : 'Save'}
                </button>
            </div>
        </div >
    );
};

export default TiltTable;